import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { AfasLiveTileContent } from "./AfasLiveTileContent";
import { navigateToAfasApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { AfasLiveInformationProvider } from "./provider";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type AfasTileContentProps = {
	renderState: TileRenderState<AppType.Afas>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	provider: AfasLiveInformationProvider;
};

export const AfasTileContent = (props: AfasTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		provider
	} = props;

	const [navigationElement, navigateHandler] = useAfasTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: { settings }
	 } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<AfasLiveTileContent
				connectionId={settings.connectionId}
				tabs={settings.tabs}
				destinationWindow={settings.destinationWindow}
				provider={provider}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
AfasTileContent.displayName = "AfasTileContent";

export function useAfasTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Afas>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		environmentAppId,
		settings: {
			destinationWindow
		}
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();

				navigateToAfasApp({
					id: environmentAppId,
					destinationWindow
				});
			},
			[
				destinationWindow,
				environmentAppId,
				registerTileUsage
			]
		)
	);
}

